import { ClientType, ContractType, PartnershipType } from '../../../types';
import { computeEffectiveRange } from '../../../utils/date-helper';
import { InsurersType } from '../bill-grid-components/bill-management-types';
import {
  AggregateHoldDataType,
  AggregateHoldsV2GridItemType,
} from './aggregate-holds-types';

/**
 * Builds one enriched grid row per aggregate-holds item (keyed on contractId).
 *
 * v2 returns one row per `contractId` with no client/insurer/partnership
 * dimensions, so we resolve those from the contract: each contract supplies the
 * clientId / insurerId / partnershipId (→ names from the already-loaded
 * clients / insurers / partnerships) and its start/end dates for the Contract
 * column. The contract is also the source of the View-Holds deep link.
 */
export const generateEnrichedAggregateHoldsData = (
  items: AggregateHoldsV2GridItemType[],
  contracts: ContractType[],
  clients: ClientType[],
  insurers: InsurersType[],
  partnerships: PartnershipType[],
): AggregateHoldDataType[] =>
  items.map((item, index) => {
    const contract = contracts.find(c => c.id === item.contractId) ?? null;

    const clientId = contract?.clientId ?? null;
    const insurerId = contract?.insurerId ?? null;
    const partnershipId = contract?.partnershipId ?? null;

    const clientName = clientId
      ? (clients.find(c => c.id === clientId)?.name ?? null)
      : null;
    const insurerName = insurerId
      ? (insurers.find(i => i.id === insurerId)?.name ?? null)
      : null;
    const partnershipName = partnershipId
      ? (partnerships.find(p => p.id === partnershipId)?.name ?? null)
      : null;

    return {
      id: index + 1,
      contractId: item.contractId,
      heldAmount: item.heldAmount,
      holds: item.holds,
      holdsCount: item.holdsCount,
      minFromDate: item.minFromDate,
      maxFromDate: item.maxFromDate,
      maxLastSeenAt: item.maxLastSeenAt,
      contractDateRange: contract
        ? computeEffectiveRange(contract.startDate, contract.endDate)
        : null,
      client: {
        id: clientId ?? 0,
        name: clientName ?? '--',
      },
      insurer: {
        id: insurerId ?? 0,
        name: insurerName ?? '--',
      },
      partnership: {
        id: partnershipId ?? 0,
        name: partnershipName ?? '--',
      },
      lastSeen: item.maxLastSeenAt,
      holdType: item.holds,
      totalHolds: item.holdsCount,
      totalImpact: item.heldAmount,
    };
  });
